// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {Modal} from 'ui'
import BillingModalRenew from './BillingModal/BillingModalRenew'
import BillingModalBlocker from './BillingModal/BillingModalBlocker'
import BillingPastDueBlocker from './BillingModal/BillingPastDueBlocker'
import BillingModalProjectBlocker from './BillingModal/BillingModalProjectBlocker'
import AccountPageConstants from 'constants/AccountPageConstants'
import { useSubscription } from '../../hooks/subscriptions/queries';
import { isPastDue, isCancelled } from '../../utils/subscriptionUtils';
import { useLocation, useHistory } from 'react-router-dom';
import { useProject } from '../../hooks/projects/queries';
import { useCurrentProject } from '../../hooks/projects/useCurrentProject';
import { useIsProjectOwner } from '../../hooks/users/useIsProjectOwner';

/**
 * Determines if a cancelled user should be blocked from viewing content
 * @returns {boolean} true if the user should be blocked
 */
function shouldBlockCancelledUser(projectId, project, isProjectOwner) {
  // No project context, nothing to block
  // This handles cases where transcript/survey hasn't loaded yet
  if (!projectId) {
    return false;
  }
  
  // No project data yet - don't block until we know
  // This handles the case where useProject(projectId) is still loading
  if (!project) {
    return false;
  }

  // User owns the project - block to encourage renewal
  if (isProjectOwner) {
    return true;
  }
  
  // Project is inactive - block access
  if (project?.is_active === false) {
    return true;
  }
  
  // User is not the owner and project is active - allow access
  return false;
}

function HeaderSubscribeModal(props) {
  const { data: subscription } = useSubscription();
  const { projectId } = useCurrentProject();
  const location = useLocation();
  const history = useHistory();
  
  const { data: project } = useProject(projectId);
  const isProjectOwner = useIsProjectOwner(project);

  const subscriptionPastDue = isPastDue(subscription);
  const subscriptionCancelled = isCancelled(subscription);
  
  // Check if we're on the users page or any sub-page
  const isOnUsersPage = location.pathname?.includes('/users');

  // Handle modal hide with navigation
  const handleHide = useCallback(() => {
    if (props.onHide) {
      props.onHide();
    }
    
    // Navigate to users page if project is not active or past due or cancelled
    if (subscriptionPastDue || subscriptionCancelled) {
      history.push('/users');
    }
  }, [props.onHide, subscriptionPastDue, subscriptionCancelled, history]);


  // Determine if modal should show based on subscription status
  // Don't show the modal on the /users page

  let pageContent = <div></div>;
  let shouldDisplayModal = false;

  // Blocking past due users - past due always blocks
  if (subscriptionPastDue && !isOnUsersPage && !props.page) {
    pageContent = <BillingPastDueBlocker onClick={handleHide}/>;
    shouldDisplayModal = true;
  }
  // Blocking cancelled users - use helper function to determine if should block
  else if (subscriptionCancelled && !isOnUsersPage && !props.page && 
           shouldBlockCancelledUser(projectId, project, isProjectOwner)) {
    pageContent = <BillingModalBlocker onClick={props.onSubscribe}/>
    shouldDisplayModal = true;
  }
  else {
    switch (props.page) {
      case AccountPageConstants.SUBSCRIPTION_PAST_DUE:
        shouldDisplayModal = true;
        pageContent = <BillingPastDueBlocker onClick={handleHide}/>
        break;
      case AccountPageConstants.UNCANCEL_SUBSCRIPTION:
        shouldDisplayModal = true;
        pageContent = <BillingModalRenew onSuccess={props.onHide}/>
        break;
      case AccountPageConstants.BLOCKER:
        shouldDisplayModal = true;
        pageContent = <BillingModalBlocker onClick={props.onSubscribe}/>
        break;
      case AccountPageConstants.PROJECT_BLOCKER:
        shouldDisplayModal = true;
        pageContent = <BillingModalProjectBlocker onClick={props.onSubscribe}/>
        break;
    }
  }

  return (
    <div>
      <Modal show={shouldDisplayModal} onHide={handleHide}>
        <Modal.Body>
          {pageContent}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default HeaderSubscribeModal;